<template>
  <div class="ai-chatbot-iframe-container">
    <iframe
      :key="src"
      v-if="!hasError"
      ref="iframe"
      :src="src"
      @load="onLoad"
      @error="handleErrorEvent"
      title="AI Chatbot Iframe"
    ></iframe>

    <component
      v-if="hasError"
      :is="errorComponent"
      @refresh="refresh"
    />
  </div>
</template>

<script>
import utils from '@/util'
import {
  IFRAME_LOAD_TIMEOUT_MS,
  IFRAME_RELOAD_QUERY_PARAM,
  IFRAME_ERROR_TYPES,
} from '@/constant/aiChatbotIframe'
import eventCategories from '@/constant/eventCategories'

export default {
  name: 'AIChatbotIframe',

  props: {
    /**
     * URL source for the AI chatbot iframe
     * @type {String}
     * @required
     */
    src: {
      type: String,
      required: true,
    },
    /**
     * Component to render when iframe fails to load
     * @type {Object|String}
     */
    errorComponent: {
      type: [Object, String],
      default: null,
    },
    /**
     * Custom timeout in milliseconds for iframe load detection
     * @type {Number}
     * @default 30000 (30 seconds)
     */
    loadTimeout: {
      type: Number,
      default: IFRAME_LOAD_TIMEOUT_MS,
    },
  },

  data() {
    return {
      hasError: false,
      loadTimeoutAbortController: null,
      errorType: null, // Tracks the type of error using IFRAME_ERROR_TYPES
    }
  },

  watch: {
    /**
     * Watch for src prop changes and reset state
     * When src changes, we need to reset error state and restart timeout
     */
    src() {
      this.resetErrorState()
      this.clearLoadTimeout()
      // Restart timeout for new src
      this.$nextTick(() => {
        this.startLoadTimeout()
      })
    },
  },

  mounted() {
    // Start timeout without blocking mounting
    this.startLoadTimeout()
  },

  beforeDestroy() {
    this.clearLoadTimeout()
  },

  methods: {
    /**
     * Resets error state to initial values
     */
    resetErrorState() {
      this.hasError = false
      this.errorType = null
    },
    /**
     * Checks if iframe appears to be blocked (e.g., by X-Frame-Options)
     * This helps detect cases where fetch succeeds but iframe embedding is blocked
     * Note: For cross-origin iframes, this detection is limited due to browser security
     * @returns {boolean} True if iframe appears blocked
     */
    isIframeBlocked() {
      const { iframe } = this.$refs
      if (!iframe) {
        return false
      }

      // Check if iframe has zero dimensions (common when blocked)
      const rect = iframe.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) {
        return true
      }

      // For same-origin iframes, check if contentWindow is null (indicates blocking)
      try {
        if (iframe.contentWindow === null) {
          return true
        }
      } catch (e) {
        // Cross-origin: can't access contentWindow, which is expected
        // This doesn't indicate blocking for cross-origin
      }

      return false
    },

    /**
     * Starts a timeout to detect if iframe fails to load
     * For cross-origin iframes, this is the primary error detection mechanism
     * Also detects X-Frame-Options blocking where fetch succeeds but iframe is blocked
     * @returns {Promise<void>}
     */
    async startLoadTimeout() {
      this.loadTimeoutAbortController = new AbortController()
      try {
        await utils.sleep(this.loadTimeout)
        // Only trigger error if timeout wasn't cancelled and load hasn't fired
        if (!this.loadTimeoutAbortController.signal.aborted && !this.hasError) {
          // Double-check: if iframe exists but hasn't loaded, it's an error
          if (this.$refs.iframe) {
            // Check if iframe appears to be blocked (e.g., X-Frame-Options)
            // This helps detect cases where fetch succeeds but embedding is blocked
            // Note: For cross-origin iframes, detection is limited due to browser security
            const isBlocked = this.isIframeBlocked()
            const errorType = isBlocked ? IFRAME_ERROR_TYPES.BLOCKED : IFRAME_ERROR_TYPES.TIMEOUT
            this.onError(errorType)
          }
        }
      } catch (error) {
        // Timeout was aborted, ignore
      }
    },

    /**
     * Clears the load timeout by aborting the AbortController
     */
    clearLoadTimeout() {
      if (this.loadTimeoutAbortController) {
        this.loadTimeoutAbortController.abort()
        this.loadTimeoutAbortController = null
      }
    },

    /**
     * Handles successful iframe load event
     * Emits onIframeReady event to parent component
     */
    onLoad() {
      this.clearLoadTimeout()
      this.resetErrorState()
      this.$emit('onIframeReady')
    },

    /**
     * Handles native @error event from iframe
     * For cross-origin iframes, this event rarely fires reliably
     * When it does fire, it indicates a real error (DNS failure, 404, network error, etc.)
     */
    handleErrorEvent() {
      // Check if iframe is blocked - blocked errors take precedence
      const isBlocked = this.isIframeBlocked()
      if (isBlocked) {
        this.setError(IFRAME_ERROR_TYPES.BLOCKED)
      } else {
        // The native @error event fired for a reason other than being blocked.
        // This is a legitimate error (e.g., DNS failure, 404, network error) and should be tracked.
        this.setError(IFRAME_ERROR_TYPES.ERROR)
      }
    },

    /**
     * Sets error state and tracks to mixpanel
     * Tracks all meaningful errors (timeout, blocked, or native error)
     * @param {string} errorType - Type of error from IFRAME_ERROR_TYPES
     */
    setError(errorType) {
      this.clearLoadTimeout()
      this.hasError = true
      this.errorType = errorType

      // Gather diagnostic information
      const diagnosticInfo = this.getErrorDiagnostics(errorType)

      // Track error to mixpanel (only meaningful errors: timeout or blocked)
      utils.trackGA4AndMixpanel('AI Chatbot Iframe Error', {
        eventAction: 'AI Chatbot Iframe Error',
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventLabel: errorType,
        ...diagnosticInfo,
      })
    },

    /**
     * Gathers diagnostic information about the iframe error
     * @param {string} errorType - Type of error from IFRAME_ERROR_TYPES
     * @returns {Object} Diagnostic properties for tracking
     */
    getErrorDiagnostics(errorType) {
      try {
        return {
          error_type: errorType,
          iframe_src: this.src,
          load_timeout_ms: this.loadTimeout,
          user_agent: navigator.userAgent,
          browser_online: navigator.onLine,
        }
      } catch (error) {
        // If error occurs while gathering diagnostic information, return empty object
        return {}
      }
    },

    /**
     * Handles iframe error with specific error type
     * Determines final error type (blocked takes precedence) and sets error
     * @param {string} errorType - Type of error from IFRAME_ERROR_TYPES
     */
    onError(errorType) {
      // Check if iframe is blocked - blocked errors take precedence
      const isBlocked = this.isIframeBlocked()
      const finalErrorType = isBlocked ? IFRAME_ERROR_TYPES.BLOCKED : errorType
      this.setError(finalErrorType)
    },

    /**
     * Appends reload query parameter to URL to force iframe reload
     * @param {string} urlString - The URL string to modify
     * @returns {string} URL with reload parameter appended
     */
    appendReloadParameter(urlString) {
      try {
        // Parse URL and append timestamp to force reload
        const url = new URL(urlString)
        url.searchParams.set(IFRAME_RELOAD_QUERY_PARAM, Date.now().toString())
        return url.toString()
      } catch (error) {
        // If URL parsing fails, try to append query string manually
        // This handles edge cases where URL constructor might fail
        const separator = urlString.includes('?') ? '&' : '?'
        return `${urlString}${separator}${IFRAME_RELOAD_QUERY_PARAM}=${Date.now()}`
      }
    },

    /**
     * Forces iframe reload by appending a timestamp query parameter
     * This ensures the iframe reloads even if the src URL hasn't changed
     */
    refresh() {
      this.resetErrorState()

      const { iframe } = this.$refs
      if (!iframe) {
        // Iframe ref doesn't exist, can't refresh
        return
      }

      iframe.src = this.appendReloadParameter(this.src)

      // Reset timeout for the refresh attempt
      this.startLoadTimeout()
    },
  },
}
</script>

<style scoped>
.ai-chatbot-iframe-container {
  width: 100%;
  height: 100%;
}

iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
